import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BadgeCheck,
  CalendarClock,
  ExternalLink,
  Loader2,
  ShieldQuestion,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import PersonAvatar from '@/components/common/PersonAvatar';
import { authenticatedBitswanBackendInstance, getAocHost } from '@/lib/api-client';

type CertificationCall = {
  id: string;
  status: 'requested' | 'scheduled' | 'held' | 'cancelled';
  status_display: string;
  preferred_times: string;
  notes: string;
  scheduled_at: string | null;
  meeting_url: string;
  host_email: string;
  requested_by_email: string;
  created_at: string;
};

type Advisor = {
  id: string;
  organization_id: string;
  display_name: string;
  headline: string;
  contact_email: string;
  country: string;
  languages: string;
  industries: string;
  years_experience: number | null;
  photo_url: string;
  certification_level: string;
  certification_display: string;
  is_certified: boolean;
  is_published: boolean;
  is_listed: boolean;
  certified_at: string | null;
  certified_by_email: string;
  client_count: number;
  training_passed: number;
  training_total: number;
  training_complete: boolean;
  call: CertificationCall | null;
  created_at: string;
};

type Roster = {
  advisors: Advisor[];
  counts: { total: number; certified: number; awaiting_call: number };
};

const FILTERS = [
  { value: '', label: 'Everyone' },
  { value: 'uncertified', label: 'Not yet certified' },
  { value: 'certified', label: 'Certified' },
];

function advisorProfileUrl(profileId: string) {
  return `${window.location.protocol}//${getAocHost(window.location.hostname)}/public/advisor/${profileId}`;
}

function when(value: string | null) {
  return value ? new Date(value).toLocaleString() : '—';
}

function toLocalInput(value: string | null) {
  if (!value) return '';
  const date = new Date(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function AdvisorRow({ advisor }: { advisor: Advisor }) {
  const queryClient = useQueryClient();
  const call = advisor.call;
  const [slot, setSlot] = React.useState(toLocalInput(call?.scheduled_at ?? null));
  const [meetingUrl, setMeetingUrl] = React.useState(call?.meeting_url ?? '');
  const [notes, setNotes] = React.useState('');

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['advisor-roster'] });
    queryClient.invalidateQueries({ queryKey: ['advisor-roster-counts'] });
  };

  const certify = useMutation({
    mutationFn: async (level: string) => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.patch(`/global-admin/advisors/${advisor.id}/certification/`, {
        certification_level: level,
      });
      return res.data;
    },
    onSuccess: (_data, level) => {
      refresh();
      toast.success(
        level === 'none'
          ? `Certification withdrawn from ${advisor.display_name}.`
          : `${advisor.display_name} is certified.`,
      );
    },
    onError: () => toast.error('Could not change that certification.'),
  });

  const updateCall = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.patch(`/global-admin/certification-calls/${call?.id}/`, patch);
      return res.data;
    },
    onSuccess: () => {
      refresh();
      toast.success('Call updated.');
    },
    onError: () => toast.error('Could not update that call.'),
  });

  const waiting = call?.status === 'requested';

  return (
    <Card
      className={waiting ? 'border-amber-300' : ''}
      data-testid={`advisor-row-${advisor.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          <PersonAvatar
            name={advisor.display_name}
            photoUrl={advisor.photo_url}
            className="h-11 w-11"
            testId="advisor-photo"
          />
          <div className="min-w-0 flex-1">
            <CardTitle className="flex flex-wrap items-center gap-2 text-base">
              <a
                href={advisorProfileUrl(advisor.id)}
                target="_blank"
                rel="noreferrer"
                className="group inline-flex items-center gap-1 hover:underline"
                data-testid="advisor-profile-link"
              >
                {advisor.display_name}
                <ExternalLink className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-70" />
              </a>
              {advisor.is_certified ? (
                <Badge
                  className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                  variant="secondary"
                  data-testid="advisor-certification"
                >
                  <BadgeCheck className="mr-1 h-3 w-3" />
                  {advisor.certification_display}
                </Badge>
              ) : (
                <Badge
                  className="bg-amber-100 text-amber-800 hover:bg-amber-100"
                  variant="secondary"
                  data-testid="advisor-certification"
                >
                  <ShieldQuestion className="mr-1 h-3 w-3" />
                  Not certified
                </Badge>
              )}
              <Badge variant="outline" data-testid="advisor-listing">
                {advisor.is_listed
                  ? 'In the directory'
                  : advisor.is_published
                    ? 'Published, not listed'
                    : 'Not published'}
              </Badge>
              {waiting && (
                <Badge className="bg-amber-600 hover:bg-amber-600" data-testid="advisor-waiting">
                  Waiting on us
                </Badge>
              )}
              {!advisor.is_certified && (
                <Badge
                  variant="outline"
                  className={advisor.training_complete ? 'border-emerald-300 text-emerald-800' : ''}
                  data-testid="advisor-training"
                >
                  Training {advisor.training_passed}/{advisor.training_total}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              {advisor.headline || 'No headline yet'}
              {advisor.contact_email ? ` · ${advisor.contact_email}` : ''}
            </CardDescription>
            <p className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Users className="h-3 w-3" />
                {advisor.client_count} client{advisor.client_count === 1 ? '' : 's'}
              </span>
              {advisor.country && <span>{advisor.country}</span>}
              {advisor.languages && <span>{advisor.languages}</span>}
              <span>joined {new Date(advisor.created_at).toLocaleDateString()}</span>
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {call && (
          <div className="space-y-3 rounded-md border bg-muted/30 p-3" data-testid="advisor-call">
            <p className="flex items-center gap-2 text-sm font-medium">
              <CalendarClock className="h-4 w-4 text-amber-600" />
              Certification call · {call.status_display}
              {call.scheduled_at && ` · ${when(call.scheduled_at)}`}
            </p>
            <p className="text-xs text-muted-foreground">
              They are free: {call.preferred_times}
              {call.notes ? ` — ${call.notes}` : ''}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor={`slot-${call.id}`} className="text-xs">
                  Put it in the diary
                </Label>
                <Input
                  id={`slot-${call.id}`}
                  type="datetime-local"
                  value={slot}
                  onChange={(event) => setSlot(event.target.value)}
                  data-testid="advisor-call-slot"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor={`url-${call.id}`} className="text-xs">
                  Meeting link
                </Label>
                <Input
                  id={`url-${call.id}`}
                  value={meetingUrl}
                  onChange={(event) => setMeetingUrl(event.target.value)}
                  placeholder="https://…"
                  data-testid="advisor-call-url"
                />
              </div>
            </div>
            <Textarea
              rows={2}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Notes for whoever takes the call."
              data-testid="advisor-call-notes"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                disabled={!slot || updateCall.isPending}
                onClick={() =>
                  updateCall.mutate({
                    scheduled_at: new Date(slot).toISOString(),
                    meeting_url: meetingUrl,
                    admin_notes: notes,
                    status: 'scheduled',
                  })
                }
                data-testid="advisor-call-schedule"
              >
                {updateCall.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                Schedule and tell them
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={updateCall.isPending}
                onClick={() => updateCall.mutate({ status: 'held', admin_notes: notes })}
                data-testid="advisor-call-held"
              >
                Mark as held
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={updateCall.isPending}
                onClick={() => updateCall.mutate({ status: 'cancelled', admin_notes: notes })}
                data-testid="advisor-call-cancel"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {advisor.is_certified ? (
            <>
              <span className="text-xs text-muted-foreground">
                Certified {when(advisor.certified_at)}
                {advisor.certified_by_email ? ` by ${advisor.certified_by_email}` : ''}
              </span>
              <Button
                size="sm"
                variant="outline"
                className="ml-auto"
                disabled={certify.isPending}
                onClick={() => certify.mutate('none')}
                data-testid="advisor-decertify"
              >
                Withdraw certification
              </Button>
            </>
          ) : (
            <>
              {!advisor.training_complete && (
                <span className="text-xs text-amber-700" data-testid="advisor-training-warning">
                  They have not finished the training
                  ({advisor.training_passed} of {advisor.training_total}).
                </span>
              )}
              <Button
                size="sm"
                className="ml-auto"
                disabled={certify.isPending}
                onClick={() => certify.mutate('basic')}
                data-testid="advisor-certify"
              >
                {certify.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                <BadgeCheck className="mr-1 h-3.5 w-3.5" />
                Grant basic certification
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdvisorsPage() {
  const [filter, setFilter] = React.useState('');

  const roster = useQuery({
    queryKey: ['advisor-roster', filter],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/global-admin/advisors/', {
        params: filter ? { certification: filter } : undefined,
      });
      return res.data as Roster;
    },
  });

  const counts = roster.data?.counts;
  const advisors = roster.data?.advisors ?? [];

  return (
    <div className="space-y-6 p-6" data-testid="advisors-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <BadgeCheck className="h-6 w-6" />
          Advisors
        </h1>
        <p className="text-sm text-muted-foreground">
          Everybody who has joined as an advisor. Only certified advisors are
          listed in the public directory or offered to customers signing up.
        </p>
      </div>

      {counts && (
        <div className="flex flex-wrap gap-3" data-testid="advisor-counts">
          <Badge variant="secondary" className="px-3 py-1">
            {counts.total} advisors
          </Badge>
          <Badge variant="secondary" className="px-3 py-1">
            {counts.certified} certified
          </Badge>
          <Badge
            className={`px-3 py-1 ${counts.awaiting_call ? 'bg-amber-600 hover:bg-amber-600' : ''}`}
            variant={counts.awaiting_call ? 'default' : 'secondary'}
            data-testid="advisor-awaiting-count"
          >
            {counts.awaiting_call} waiting for a call
          </Badge>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((option) => (
          <Button
            key={option.value || 'all'}
            size="sm"
            variant={filter === option.value ? 'default' : 'outline'}
            onClick={() => setFilter(option.value)}
            data-testid={`advisor-filter-${option.value || 'all'}`}
          >
            {option.label}
          </Button>
        ))}
      </div>

      {roster.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : advisors.length === 0 ? (
        <p className="py-16 text-center text-sm text-muted-foreground" data-testid="advisors-empty">
          Nobody here.
        </p>
      ) : (
        <div className="space-y-4">
          {advisors.map((advisor) => (
            <AdvisorRow key={advisor.id} advisor={advisor} />
          ))}
        </div>
      )}
    </div>
  );
}
